// ets_tracing: off
import { Tagged } from "@effect-ts/core/Case";
import * as A from "@effect-ts/core/Collections/Immutable/Array";
import * as R from "@effect-ts/core/Collections/Immutable/Dictionary";
import * as E from "@effect-ts/core/Either";
import { constant, flow, identity, pipe } from "@effect-ts/core/Function";
import * as O from "@effect-ts/core/Option";
import * as DSL from "@effect-ts/core/Prelude/DSL";
import { matchTag_ } from "@effect-ts/system/Utils";
export class Lens extends Tagged("Lens") {
  constructor() {
    super(...arguments);

    this[">>>"] = ab => // @ts-expect-error
    matchTag_(ab, {
      Iso: ab => lensComposeLens(isoAsLens(ab))(this),
      Lens: ab => lensComposeLens(ab)(this),
      Optional: ab => optionalComposeOptional(ab)(lensAsOptional(this)),
      Prism: ab => lensComposePrism(ab)(this),
      Traversal: ab => traversalComposeTraversal(ab)(lensAsTraversal(this))
    });
  }

}
export class Prism extends Tagged("Prism") {
  constructor() {
    super(...arguments);

    this[">>>"] = ab => //@ts-expect-error
    matchTag_(ab, {
      Iso: ab => composePrism(isoAsPrism(ab))(this),
      Lens: ab => prismComposeLens(ab)(this),
      Optional: ab => optionalComposeOptional(ab)(prismAsOptional(this)),
      Prism: ab => composePrism(ab)(this),
      Traversal: ab => traversalComposeTraversal(ab)(prismAsTraversal(this))
    });
  }

}
export class Optional extends Tagged("Optional") {
  constructor() {
    super(...arguments);

    this[">>>"] = ab => // @ts-expect-error
    matchTag_(ab, {
      Iso: ab => optionalComposeOptional(isoAsOptional(ab))(this),
      Lens: ab => optionalComposeOptional(lensAsOptional(ab))(this),
      Optional: ab => optionalComposeOptional(ab)(this),
      Prism: ab => optionalComposeOptional(prismAsOptional(ab))(this),
      Traversal: ab => traversalComposeTraversal(ab)(optionalAsTraversal(this))
    });
  }

}
export class Iso extends Tagged("Iso") {
  constructor() {
    super(...arguments);

    this[">>>"] = ab => // @ts-expect-error
    matchTag_(ab, {
      Iso: ab => isoComposeIso(ab)(this),
      Lens: ab => lensComposeLens(ab)(isoAsLens(this)),
      Optional: ab => optionalComposeOptional(ab)(isoAsOptional(this)),
      Prism: ab => composePrism(ab)(isoAsPrism(this)),
      Traversal: ab => traversalComposeTraversal(ab)(isoAsTraversal(this))
    });
  }

}
export class Traversal extends Tagged("Traversal") {
  constructor() {
    super(...arguments);

    this[">>>"] = ab => matchTag_(ab, {
      Iso: ab => traversalComposeTraversal(isoAsTraversal(ab))(this),
      Lens: ab => traversalComposeTraversal(lensAsTraversal(ab))(this),
      Optional: ab => traversalComposeTraversal(optionalAsTraversal(ab))(this),
      Prism: ab => traversalComposeTraversal(prismAsTraversal(ab))(this),
      Traversal: ab => traversalComposeTraversal(ab)(this)
    });
  }

} // -------------------------------------------------------------------------------------
// Iso
// -------------------------------------------------------------------------------------

export const isoAsLens = sa => new Lens({
  get: sa.get,
  set: flow(sa.reverseGet, constant)
});
export const isoAsOptional = sa => new Optional({
  getOption: flow(sa.get, O.some),
  set: flow(sa.reverseGet, constant)
});
/**
 * Compose an `Iso` with an `Iso`
 */

export const isoComposeIso = ab => sa => new Iso({
  get: flow(sa.get, ab.get),
  reverseGet: flow(ab.reverseGet, sa.reverseGet)
});
/**
 * View an `Iso` as a `Prism`
 */

export const isoAsPrism = sa => new Prism({
  getOption: flow(sa.get, O.some),
  reverseGet: sa.reverseGet
});
export const isoAsTraversal = sa => new Traversal({
  modifyF: F => f => s => F.map(a => sa.reverseGet(a))(f(sa.get(s)))
}); // -------------------------------------------------------------------------------------
// Lens
// -------------------------------------------------------------------------------------

export const lensAsOptional = sa => new Optional({
  getOption: flow(sa.get, O.some),
  set: sa.set
});
export const lensAsTraversal = sa => new Traversal({
  modifyF: F => f => s => F.map(a => sa.set(a)(s))(f(sa.get(s)))
});
export const lensComposeLens = ab => sa => new Lens({
  get: s => ab.get(sa.get(s)),
  set: b => s => sa.set(ab.set(b)(sa.get(s)))(s)
});
export const lensComposePrism = ab => sa => optionalComposeOptional(prismAsOptional(ab))(lensAsOptional(sa));
export const lensId = () => new Lens({
  get: identity,
  set: constant
});
export const lensProp = prop => lens => new Lens({
  get: s => lens.get(s)[prop],
  set: ap => s => {
    const oa = lens.get(s);

    if (ap === oa[prop]) {
      return s;
    }

    return lens.set(Object.assign({}, oa, {
      [prop]: ap
    }))(s);
  }
});
export const lensProps = (...props) => lens => new Lens({
  get: s => {
    const a = lens.get(s);
    const r = {};

    for (const k of props) {
      r[k] = a[k];
    }

    return r;
  },
  set: a => s => {
    const oa = lens.get(s);
    const b = {};
    let mod = false;

    for (const k of props) {
      if (a[k] !== oa[k]) {
        mod = true;
        b[k] = a[k];
      }
    }

    if (mod) {
      return lens.set(Object.assign({}, oa, b))(s);
    }

    return s;
  }
});
export const lensComponent = prop => lens => new Lens({
  get: s => lens.get(s)[prop],
  set: ap => s => {
    const oa = lens.get(s);

    if (ap === oa[prop]) {
      return s;
    }

    const copy = oa.slice();
    copy[prop] = ap;
    return lens.set(copy)(s);
  }
}); // -------------------------------------------------------------------------------------
// Prism
// -------------------------------------------------------------------------------------

export const prismAsOptional = sa => new Optional({
  getOption: sa.getOption,
  set: a => prismSet(a)(sa)
});
export const prismAsTraversal = sa => new Traversal({
  modifyF: F => {
    const succeed = DSL.succeedF(F);
    return f => s => O.fold_(sa.getOption(s), () => succeed(s), a => F.map(a => prismSet(a)(sa)(s))(f(a)));
  }
});
export const prismModifyOption = f => sa => s => O.map_(sa.getOption(s), o => {
  const n = f(o);
  return n === o ? s : sa.reverseGet(n);
});
export const prismModify = f => sa => {
  const g = prismModifyOption(f)(sa);
  return s => O.getOrElse_(g(s), () => s);
};
export const prismSet = a => prismModify(() => a);
export const prismComposeLens = ab => sa => optionalComposeOptional(lensAsOptional(ab))(prismAsOptional(sa));
export const prismFromNullable = () => new Prism({
  getOption: O.fromNullable,
  reverseGet: identity
});
export function prismFromPredicate(predicate) {
  return new Prism({
    getOption: O.fromPredicate(predicate),
    reverseGet: identity
  });
}
export const prismSome = () => new Prism({
  getOption: identity,
  reverseGet: O.some
});
export const prismRight = () => new Prism({
  getOption: O.fromEither,
  reverseGet: E.right
});
export const prismLeft = () => new Prism({
  getOption: s => E.isLeft(s) ? O.some(s.left) : O.none,
  reverseGet: E.left
}); // -------------------------------------------------------------------------------------
// Optional
// -------------------------------------------------------------------------------------

/**
 * Compose a `Prism` with a `Prism`
 */

export const composePrism = ab => sa => new Prism({
  getOption: flow(sa.getOption, O.chain(ab.getOption)),
  reverseGet: flow(ab.reverseGet, sa.reverseGet)
});
export const optionalAsTraversal = sa => new Traversal({
  modifyF: F => f => {
    const succeed = DSL.succeedF(F);
    return s => O.fold_(sa.getOption(s), () => succeed(s), a => F.map(a => sa.set(a)(s))(f(a)));
  }
});
export const optionalModifyOption = f => optional => s => O.map_(optional.getOption(s), a => {
  const n = f(a);
  return n === a ? s : optional.set(n)(s);
});
export const optionalModify = f => optional => {
  const g = optionalModifyOption(f)(optional);
  return s => O.getOrElse_(g(s), () => s);
};
export const optionalComposeOptional = ab => sa => new Optional({
  getOption: flow(sa.getOption, O.chain(ab.getOption)),
  set: b => optionalModify(ab.set(b))(sa)
});

const findMutable = predicate => new Optional({
  getOption: A.find(predicate),
  set: a => s => O.fold_(A.findIndex_(s, predicate), () => s, i => A.unsafeUpdateAt(i, a)(s))
});

export const find = findMutable; // -------------------------------------------------------------------------------------
// Traversal
// -------------------------------------------------------------------------------------

export function traversalComposeTraversal(ab) {
  return sa => new Traversal({
    modifyF: F => f => sa.modifyF(F)(ab.modifyF(F)(f))
  });
}
export function fromForEach(T) {
  return () => new Traversal({
    modifyF: T.forEachF
  });
} // -------------------------------------------------------------------------------------
// Ix
// -------------------------------------------------------------------------------------

function indexMutableArray() {
  return {
    index: i => new Optional({
      getOption: A.get(i),
      set: a => as => O.getOrElse_(A.updateAt(i, a)(as), () => as)
    })
  };
}

export const indexArray = indexMutableArray;
export function indexRecord() {
  return {
    index: k => new Optional({
      getOption: R.lookup(k),
      set: a => r => {
        if (r[k] === a || O.isNone(R.lookup(k)(r))) {
          return r;
        }

        return R.insertAt(k, a)(r);
      }
    })
  };
} // -------------------------------------------------------------------------------------
// At
// -------------------------------------------------------------------------------------

export function atRecord() {
  return {
    at: key => new Lens({
      get: R.lookup(key),
      set: O.fold(() => R.deleteAt(key), a => R.insertAt(key, a))
    })
  };
}
//# sourceMappingURL=index.mjs.map